import React, { useState, useEffect } from 'react';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  Flex,
  Box,
  Text,
  Spinner,
  Badge,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tooltip,
  Select,
  Input,
  useToast,
} from '@chakra-ui/react';
import { DownloadSimple, ArrowSquareOut, WarningCircle } from 'phosphor-react';
import {
  IEmpresaCard,
  useConferenciaNotas,
  INotaConferencia,
  INotasKpi,
  LadoNota,
  LadoFiltro,
  BuscaCampo,
  Confianca,
  SituacaoFiltro,
  INotasKpiSituacao,
} from '../../../../hooks/useConferenciaFiscal';
import { api } from '../../../../services/api';
import { formatCPFOrCNPJ } from '../../../../utils/FormatFunctions';

interface Props {
  empresa: IEmpresaCard | null;
  range: { data_ini?: string; data_fim?: string };
  onClose: () => void;
}

type Aba = 'saida' | 'entrada' | 'servico';
const ABAS: Array<{ k: Aba; lab: string }> = [
  { k: 'entrada', lab: 'Entradas' },
  { k: 'saida', lab: 'Saídas' },
  { k: 'servico', lab: 'Serviços' },
];
const ABA_LAB: Record<Aba, string> = { saida: 'Saída', entrada: 'Entrada', servico: 'Serviço' };

// Modos de busca (coluna) e tipos de documento pros seletores do drawer.
const BUSCA_MODOS: Array<{ v: BuscaCampo; lab: string }> = [
  { v: 'todos', lab: 'Tudo' },
  { v: 'numero', lab: 'Número' },
  { v: 'contraparte', lab: 'Contraparte' },
  { v: 'cnpj', lab: 'CNPJ' },
  { v: 'valor', lab: 'Valor' },
  { v: 'chave', lab: 'Chave' },
];
const TIPO_DOC_OPCOES: Array<{ v: string; lab: string }> = [
  { v: '', lab: 'Todos os tipos' },
  { v: 'nfe', lab: 'NF-e' },
  { v: 'nfce', lab: 'NFC-e' },
  { v: 'cte', lab: 'CT-e' },
  { v: 'nfse', lab: 'NFS-e' },
];
const LADO_CSV: Record<LadoNota, string> = {
  so_dominio: 'Só no Domínio',
  so_fiscal: 'Só na outra fonte',
  par: 'Conferida (par)',
  fora_escopo: 'Fora da comparação',
};
// Rótulo por tipo de devolução (própria = saída / de venda = entrada).
const DEVOLUCAO_LABEL: Record<string, string> = {
  propria: 'devolução própria',
  venda: 'devolução de venda',
};
const DEVOLUCAO_CSV: Record<string, string> = { propria: 'Propria', venda: 'Venda' };

// Filtros de situação (chips). `kpi` aponta a contagem correspondente no payload.
const SITUACOES: Array<{
  k: SituacaoFiltro;
  kpi: keyof INotasKpiSituacao;
  lab: string;
  cor: string;
  dica: string;
}> = [
  {
    k: 'cancelada',
    kpi: 'canceladas',
    lab: 'Canceladas',
    cor: '#b91c1c',
    dica: 'Notas canceladas em pelo menos uma das fontes.',
  },
  {
    k: 'divergente',
    kpi: 'divergentes',
    lab: 'Situação divergente',
    cor: '#b45309',
    dica: 'A nota existe nas duas fontes, mas cancelada só em uma — é o caso em que a contagem bate e as notas não.',
  },
  {
    k: 'ativa',
    kpi: 'ativas',
    lab: 'Ativas',
    cor: '#047857',
    dica: 'Notas autorizadas em todas as fontes onde aparecem.',
  },
];

// Situação por FONTE. Antes existia um único selo "cancelada" (que vinha do
// Domínio), então dava pra saber que havia N canceladas no card sem descobrir
// QUAIS eram — e uma nota cancelada só na outra fonte aparecia como autorizada.
const SinaisSituacao = ({ n }: { n: INotaConferencia }) => {
  if (!n.cancelada) return null;
  const selo = (lab: string, canc: boolean) => (
    <Badge
      key={lab}
      bg={canc ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.12)'}
      color={canc ? '#b91c1c' : '#047857'}
      textTransform="none"
      fontSize="10.5px"
    >
      {lab}: {canc ? 'cancelada' : 'autorizada'}
    </Badge>
  );
  const fonte = fonteDaNota(n);
  return (
    <>
      {/* divergente = existe nos dois lados com situação diferente: é o caso que
          faz a contagem bater e as notas não. Mostra os dois lados lado a lado. */}
      {n.situacao_divergente && (
        <Tooltip
          label={`Situação divergente: no Domínio está ${
            n.cancelada_dominio ? 'cancelada' : 'autorizada'
          } e no ${fonte} está ${n.cancelada_fonte ? 'cancelada' : 'autorizada'}.`}
          fontSize="11px"
          hasArrow
          placement="top"
          bg="#0F172A"
          color="#F1F5F9"
          borderRadius="lg"
          px={3}
          py={2}
          maxW="250px"
        >
          <Badge
            bg="rgba(239,68,68,0.16)"
            color="#b91c1c"
            textTransform="none"
            fontSize="10.5px"
            fontWeight={800}
            cursor="help"
          >
            ⚠ situação divergente
          </Badge>
        </Tooltip>
      )}
      {n.cancelada_dominio != null && selo('Domínio', n.cancelada_dominio)}
      {n.cancelada_fonte != null && selo(fonte, n.cancelada_fonte)}
    </>
  );
};

// Confronta uma vez por aba; os lotes da rota paginada refaziam toda a comparacao.
async function buscarTodasNotas(
  empresaId: number,
  tipo: Aba,
  range: { data_ini?: string; data_fim?: string },
): Promise<INotaConferencia[]> {
  const res = await api.get<string>(
    `/api/v1/conferencia-fiscal/empresas/${empresaId}/notas/stream`,
    { params: { ...range, tipo }, responseType: 'text' },
  );
  const registros = res.data
    .split('\n')
    .filter(linha => linha.trim())
    .map(linha => JSON.parse(linha));
  const meta = registros[0];
  const fim = registros[registros.length - 1];
  const notas = registros.filter(r => r.evento === 'nota').map(r => r.data);
  // Nao baixar um CSV parcial como se a transferencia tivesse terminado.
  if (
    meta?.evento !== 'meta' ||
    fim?.evento !== 'fim' ||
    meta?.data?.total !== notas.length ||
    fim?.data?.total_entregue !== notas.length
  ) {
    throw new Error('Exportação incompleta');
  }
  return notas;
}

const csvEscape = (v: unknown): string => {
  const s = v == null ? '' : String(v);
  return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const montaCSV = (linhas: Array<{ aba: Aba; nota: INotaConferencia }>): string => {
  const cols = [
    'Aba',
    'Tipo',
    'Numero',
    'Serie',
    'Modelo',
    'Emissao',
    'Valor',
    'Contraparte',
    'CNPJ',
    'Conferencia',
    'Confianca',
    'Cancelada',
    'Cancelada_Dominio',
    'Cancelada_Fonte',
    'Situacao_Divergente',
    'Devolucao',
    'Chave',
  ];
  const simNaoNulo = (v: boolean | null) => (v == null ? '' : (v && 'Sim') || 'Nao');
  const linhaCsv = ({ aba, nota }: { aba: Aba; nota: INotaConferencia }) =>
    [
      ABA_LAB[aba],
      (nota.tipo || '').toUpperCase(),
      nota.numero ?? '',
      nota.serie ?? '',
      nota.modelo ?? '',
      nota.data_emissao ?? '',
      nota.valor == null ? '' : nota.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 }),
      nota.contraparte_nome ?? '',
      nota.contraparte_cnpj ?? '',
      LADO_CSV[nota.lado],
      nota.confianca ?? '',
      nota.cancelada ? 'Sim' : 'Nao',
      simNaoNulo(nota.cancelada_dominio),
      simNaoNulo(nota.cancelada_fonte),
      nota.situacao_divergente ? 'Sim' : 'Nao',
      nota.devolucao ? (DEVOLUCAO_CSV[nota.devolucao_tipo ?? ''] ?? 'Sim') : 'Nao',
      nota.chave ?? '',
    ]
      .map(csvEscape)
      .join(';');
  const bom = String.fromCharCode(0xfeff); // Excel abre UTF-8 com acento
  return `${bom}${cols.join(';')}\n${linhas.map(linhaCsv).join('\n')}`;
};

const baixarArquivo = (nome: string, conteudo: string) => {
  const blob = new Blob([conteudo], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  a.click();
  URL.revokeObjectURL(url);
};

const fmtMoney = (n: number | null) =>
  n == null ? '—' : n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// Fonte "outra": NF-e/NFC-e/CT-e sempre vêm do Autmais. NFS-e tem 2 fontes
// (Autmais/Portal) e o backend já diz qual delas casou com ESTA nota
// (`n.fonte`, ver união Autmais∪Portal em _notas_servico) — usar um valor fixo
// "Portal" pra toda NFS-e escondia quando a nota real veio do Autmais.
const fonteDaNota = (n: INotaConferencia) => {
  if (n.tipo !== 'nfse') return 'Autmais';
  return n.fonte === 'autmais' ? 'Autmais' : 'Portal';
};

// Link pra tela do Portal Nacional (nfseManagement) já filtrada: mesma empresa
// (codi_emp = external_id), mesmo período e, se houver, o número da NFS-e.
const portalNacionalUrl = (
  externalId: number,
  range: { data_ini?: string; data_fim?: string },
  numero?: string | null,
) => {
  const p = new URLSearchParams();
  if (range.data_ini) p.set('date_start', range.data_ini);
  if (range.data_fim) p.set('date_end', range.data_fim);
  if (numero) p.set('filtro_numero_nfse', numero);
  return `/modulesHome/integration/nfseManagement/${externalId}?${p.toString()}`;
};

// Link pra Auditoria Inteligente (o Domínio), já filtrada: empresa (id interno),
// período (YYYY-MM), aba (tipo) e, se houver, o número da nota na busca.
const auditoriaDominioUrl = (
  empresaId: number,
  range: { data_ini?: string; data_fim?: string },
  tipo: Aba,
  numero?: string | null,
) => {
  const p = new URLSearchParams();
  p.set('empresa', String(empresaId));
  p.set('escopo', 'empresa');
  if (range.data_ini) p.set('ini', range.data_ini.slice(0, 7));
  if (range.data_fim) p.set('fim', range.data_fim.slice(0, 7));
  p.set('tipo', tipo);
  if (numero) p.set('busca', numero);
  return `/modulesHome/auditoria-inteligente/notas?${p.toString()}`;
};
// Cores por fonte (mesmo padrão dos cards).
const FONTE_COR: Record<string, string> = {
  Domínio: '#1B88F5',
  'Domínio (pendente)': '#94A3B8',
  Autmais: '#b45309',
  Portal: '#6d28d9',
};
// Em quais fontes a nota está: par mostra as duas; divergência mostra só uma.
// `pendente_reextracao` (NFS-e de entrada) é outro caso de "está nas duas": a
// nota já chegou no Domínio (note-list bruto) mas a re-extração de impostos
// ainda não processou ela — ver ADR-004. Sem isso a nota aparecia como "só
// Portal/Autmais" mesmo já estando importada, só não confirmada ainda.
//
// `fora_escopo` NÃO é sempre Domínio: pra NF-e/NFC-e/CT-e é sempre fatura
// (modelo 42, documento que só existe no Domínio) — mas NFS-e não tem modelo
// 42, então pra ela `fora_escopo` é outra coisa: nota cancelada que existe SÓ
// na fonte externa (Autmais/Portal), nunca chegou no Domínio (achado real:
// nota 1700/empresa 1326, cancelada no Portal, zero registro em qualquer
// tabela do Domínio). Marcar "Domínio" ali era falso, e a fonte real
// (Autmais/Portal, que o backend já manda em `n.fonte`) nem aparecia.
const fontesDaNota = (n: INotaConferencia): string[] => {
  const fontes: string[] = [];
  const foraEscopoEhDominio = n.lado === 'fora_escopo' && n.tipo !== 'nfse';
  if (n.lado === 'par' || n.lado === 'so_dominio' || foraEscopoEhDominio) {
    fontes.push('Domínio');
  } else if (n.pendente_reextracao) {
    fontes.push('Domínio (pendente)');
  }
  if (
    n.lado === 'par' ||
    n.lado === 'so_fiscal' ||
    (n.lado === 'fora_escopo' && n.tipo === 'nfse')
  ) {
    fontes.push(fonteDaNota(n));
  }
  return fontes;
};

// KPI clicável: totais por fonte + divergências + fora-de-comparação. Ao clicar,
// filtra a lista pelo lado; clicar de novo limpa. Cores no padrão do card.
interface ChipDef {
  k: LadoFiltro;
  lab: string;
  cor: string;
  valor: number;
  dica: string;
}
const montaChips = (kpi: INotasKpi, fonte: string, fonteCor: string): ChipDef[] => {
  const base: ChipDef[] = [
    {
      k: 'dominio',
      lab: 'Domínio',
      cor: '#1B88F5',
      valor: kpi.dominio,
      dica: 'Notas no Domínio (conferidas + só no Domínio + fora da comparação)',
    },
    {
      k: 'fiscal',
      lab: fonte,
      cor: fonteCor,
      valor: kpi.fiscal,
      dica: `Notas no ${fonte} (conferidas + só no ${fonte})`,
    },
    {
      k: 'par',
      lab: 'Conferidas',
      cor: '#10B981',
      valor: kpi.par,
      dica: 'Bateram nas duas fontes (número + série)',
    },
    {
      k: 'so_dominio',
      lab: 'Só no Domínio',
      cor: '#f59e0b',
      valor: kpi.so_dominio,
      dica: `No Domínio mas não no ${fonte} — a outra fonte não capturou`,
    },
    {
      k: 'so_fiscal',
      lab: `Só no ${fonte}`,
      cor: '#ef4444',
      valor: kpi.so_fiscal,
      dica: `No ${fonte} mas não no Domínio — falta lançar`,
    },
  ];
  if (kpi.fora_escopo > 0) {
    base.push({
      k: 'fora_escopo',
      lab: 'Fora da comparação',
      cor: '#64748b',
      valor: kpi.fora_escopo,
      dica: 'Fatura (cod. 42) e outras espécies que não entram na conferência',
    });
  }
  return base;
};

const KpiChip = ({ c, ativo, onClick }: { c: ChipDef; ativo: boolean; onClick: () => void }) => (
  <Tooltip
    label={c.dica}
    fontSize="11px"
    hasArrow
    placement="top"
    bg="#0F172A"
    color="#F1F5F9"
    borderRadius="lg"
    px={3}
    py={2}
    maxW="230px"
    openDelay={300}
  >
    <Box
      as="button"
      onClick={onClick}
      textAlign="left"
      px="11px"
      py="7px"
      borderRadius="11px"
      border="1.5px solid"
      borderColor={ativo ? c.cor : 'rgba(15,23,42,0.08)'}
      bg={ativo ? `${c.cor}18` : 'rgba(255,255,255,0.55)'}
      transition="all 0.15s"
      _hover={{ borderColor: c.cor, bg: `${c.cor}12` }}
      minW="92px"
      flex="1 1 92px"
    >
      <Text
        fontSize="17px"
        fontWeight={800}
        color={c.cor}
        lineHeight="1.1"
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {c.valor.toLocaleString('pt-BR')}
      </Text>
      <Text fontSize="10.5px" fontWeight={600} color="#64748b" mt="1px" noOfLines={1}>
        {c.lab}
      </Text>
    </Box>
  </Tooltip>
);

const CONF_COR: Record<Confianca, string> = { alta: '#10B981', media: '#F59E0B', baixa: '#EF4444' };
const CONF_W: Record<Confianca, string> = { alta: '100%', media: '60%', baixa: '30%' };
const confCor = (c: Confianca | null) => (c ? CONF_COR[c] : '#CBD5E1');

const LinkFonte = ({
  href,
  cor,
  corHover,
  label,
}: {
  href: string;
  cor: string;
  corHover: string;
  label: string;
}) => (
  <Tooltip label={label} fontSize="11px" hasArrow openDelay={200}>
    <Box
      as="a"
      href={href}
      target="_blank"
      rel="noreferrer"
      color={cor}
      display="inline-flex"
      _hover={{ color: corHover }}
      onClick={e => e.stopPropagation()}
    >
      <ArrowSquareOut size={14} weight="bold" />
    </Box>
  </Tooltip>
);

const Linha = ({
  n,
  portalHref,
  dominioHref,
}: {
  n: INotaConferencia;
  portalHref?: string | null;
  dominioHref?: string | null;
}) => {
  const fontes = fontesDaNota(n);
  return (
    <Tr _hover={{ bg: 'rgba(27,136,245,0.035)' }}>
      <Td>
        <Text fontWeight={700} textTransform="uppercase" fontSize="12px">
          {n.tipo}
        </Text>
      </Td>
      <Td fontFamily="mono" fontSize="12px">
        <Flex align="center" gap={1.5}>
          <span>
            {n.numero || '—'}
            {n.serie ? ` · ${n.serie}` : ''}
          </span>
          {dominioHref && (
            <LinkFonte
              href={dominioHref}
              cor="#1B88F5"
              corHover="#0b5cad"
              label="Abrir no Domínio (Auditoria)"
            />
          )}
          {portalHref && (
            <LinkFonte
              href={portalHref}
              cor="#6d28d9"
              corHover="#4c1d95"
              label="Abrir no Portal Nacional"
            />
          )}
        </Flex>
      </Td>
      <Td fontFamily="mono" fontSize="12px">
        {n.data_emissao || '—'}
      </Td>
      <Td isNumeric fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {fmtMoney(n.valor)}
      </Td>
      <Td fontSize="12px" maxW="200px" isTruncated>
        {/* Notas só no Portal (NFS-e tomada) vêm sem o nome do prestador — o
            Portal Nacional traz só o CNPJ. Cai pro CNPJ formatado pra não ficar
            "—" com a nota anônima. */}
        {n.contraparte_nome || (n.contraparte_cnpj ? formatCPFOrCNPJ(n.contraparte_cnpj) : '—')}
      </Td>
      <Td>
        <Flex gap={1} wrap="wrap" align="center">
          {fontes.map(f => (
            <Box
              key={f}
              px="7px"
              py="1px"
              borderRadius="6px"
              fontSize="10.5px"
              fontWeight={700}
              color={FONTE_COR[f]}
              bg={`${FONTE_COR[f]}18`}
            >
              {f}
            </Box>
          ))}
          {n.lado === 'fora_escopo' && (
            <Text fontSize="10px" color="#94A3B8" fontStyle="italic">
              fora
            </Text>
          )}
        </Flex>
      </Td>
      <Td>
        <Flex gap={1} wrap="wrap">
          {n.devolucao && (
            <Badge
              bg="rgba(139,92,246,0.12)"
              color="#6d28d9"
              textTransform="none"
              fontSize="10.5px"
            >
              {DEVOLUCAO_LABEL[n.devolucao_tipo ?? ''] ?? 'devolução'}
            </Badge>
          )}
          <SinaisSituacao n={n} />
          {!n.devolucao && !n.cancelada && <Text color="#CBD5E1">—</Text>}
        </Flex>
      </Td>
      <Td>
        {n.confianca ? (
          <Flex align="center" gap={1.5} fontSize="11.5px" fontWeight={700}>
            <Box w="34px" h="6px" borderRadius="4px" bg="rgba(15,23,42,0.08)" overflow="hidden">
              <Box
                h="100%"
                borderRadius="4px"
                bg={confCor(n.confianca)}
                w={n.confianca ? CONF_W[n.confianca] : '30%'}
              />
            </Box>
            {n.confianca}
          </Flex>
        ) : (
          <Text color="#CBD5E1">—</Text>
        )}
      </Td>
      <Td>
        {n.chave ? (
          <Text
            fontFamily="mono"
            fontSize="10px"
            color="#64748B"
            maxW="130px"
            isTruncated
            cursor="pointer"
            title={`${n.chave} — clique para copiar`}
            onClick={() => navigator.clipboard?.writeText(n.chave ?? '')}
          >
            {n.chave}
          </Text>
        ) : (
          <Text color="#CBD5E1">—</Text>
        )}
      </Td>
    </Tr>
  );
};

const PAGE_SIZE = 100;

export const NotasDrawer = ({ empresa, range, onClose }: Props) => {
  const toast = useToast();
  const [aba, setAba] = useState<Aba>('entrada');
  const [pag, setPag] = useState(0);
  const [exportando, setExportando] = useState(false);
  const [ladoFiltro, setLadoFiltro] = useState<LadoFiltro | null>(null);
  const [situacao, setSituacao] = useState<SituacaoFiltro | null>(null);
  const [tipoDoc, setTipoDoc] = useState('');
  const [buscaCampo, setBuscaCampo] = useState<BuscaCampo>('todos');
  const [buscaInput, setBuscaInput] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');

  // debounce: só consulta 550ms após parar de digitar (não a cada tecla)
  useEffect(() => {
    const t = setTimeout(() => {
      setBuscaAplicada(buscaInput.trim());
      setPag(0);
    }, 550);
    return () => clearTimeout(t);
  }, [buscaInput]);

  const exportar = async () => {
    if (!empresa || exportando) return;
    setExportando(true);
    try {
      const abas: Aba[] = ['entrada', 'saida', 'servico'];
      const porAba = await Promise.all(
        abas.map(ab => buscarTodasNotas(empresa.empresa_id, ab, range).then(ns => ({ ab, ns }))),
      );
      const linhas = porAba.flatMap(({ ab, ns }) => ns.map(nota => ({ aba: ab, nota })));
      const per = range.data_ini ? range.data_ini.slice(0, 7) : 'periodo';
      baixarArquivo(`conferencia_${empresa.external_id}_${per}.csv`, montaCSV(linhas));
    } catch {
      toast({
        title: 'Não foi possível concluir a exportação.',
        description: 'Tente novamente.',
        status: 'error',
        isClosable: true,
      });
    } finally {
      setExportando(false);
    }
  };

  const notas = useConferenciaNotas(empresa?.empresa_id ?? null, {
    ...range,
    tipo: aba,
    lado: ladoFiltro ?? undefined,
    tipo_doc: aba === 'servico' ? undefined : tipoDoc || undefined,
    busca: buscaAplicada || undefined,
    busca_campo: buscaCampo,
    situacao: situacao ?? undefined,
    limit: PAGE_SIZE,
    offset: pag * PAGE_SIZE,
  });
  const total = notas.data?.total ?? 0;
  const totalPag = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const kpi = notas.data?.kpi;
  const kpiSit = notas.data?.kpi_situacao;
  // Rótulo da "outra fonte" segue o que o backend comparou: NFS-e casa com o
  // Portal, mercadoria com o Autmais. Antes era fixo "Autmais" na aba Entradas,
  // então a NFS-e tomada (que vem do Portal) aparecia rotulada como Autmais.
  const fonteFiscal = aba === 'servico' ? 'portal' : (notas.data?.fonte_fiscal ?? 'autmais');
  const FONTE_LAB: Record<string, string> = {
    portal: 'Portal',
    autmais: 'Autmais',
    misto: 'Autmais/Portal',
  };
  const fonteLab = FONTE_LAB[fonteFiscal] ?? 'Autmais';
  const fonteCor = fonteFiscal === 'portal' ? '#6d28d9' : '#b45309';

  const clicaChip = (k: LadoFiltro) => {
    setLadoFiltro(prev => (prev === k ? null : k));
    setPag(0);
  };
  const clicaSituacao = (k: SituacaoFiltro) => {
    setSituacao(prev => (prev === k ? null : k));
    setPag(0);
  };

  // trocar de empresa/aba recomeça na primeira página e limpa os filtros
  useEffect(() => {
    setPag(0);
    setLadoFiltro(null);
    setSituacao(null);
    setTipoDoc('');
    setBuscaInput('');
    setBuscaAplicada('');
  }, [empresa?.empresa_id, aba]);

  const renderCorpo = () => {
    if (notas.isLoading) {
      return (
        <Flex justify="center" py={16}>
          <Spinner color="#1B88F5" />
        </Flex>
      );
    }
    if (notas.isError) {
      return (
        <Text color="#b91c1c" py={8}>
          Não foi possível carregar as notas.
        </Text>
      );
    }
    if (!notas.data?.itens.length) {
      return (
        <Text textAlign="center" color="#94A3B8" py={12}>
          {ladoFiltro || buscaAplicada || tipoDoc || situacao
            ? 'Nenhuma nota para os filtros aplicados.'
            : `Nenhuma nota de ${aba} neste período.`}
        </Text>
      );
    }
    return (
      <>
        <Box overflowX="auto" borderRadius="14px">
          <Table size="sm" minW="820px">
            <Thead>
              <Tr>
                <Th>Modelo</Th>
                <Th>Número·Série</Th>
                <Th>Emissão</Th>
                <Th isNumeric>Valor</Th>
                <Th>Contraparte</Th>
                <Th>Fontes</Th>
                <Th>Sinais</Th>
                <Th>Confiança</Th>
                <Th>Chave</Th>
              </Tr>
            </Thead>
            <Tbody>
              {notas.data.itens.map(n => {
                const emDominio = n.lado !== 'so_fiscal';
                const noPortal =
                  n.tipo === 'nfse' && n.lado !== 'so_dominio' && n.lado !== 'fora_escopo';
                return (
                  <Linha
                    key={`${n.lado}-${n.tipo}-${n.numero ?? ''}-${n.serie ?? ''}-${n.chave ?? ''}`}
                    n={n}
                    dominioHref={
                      emDominio && empresa
                        ? auditoriaDominioUrl(empresa.empresa_id, range, aba, n.numero)
                        : null
                    }
                    portalHref={
                      noPortal && empresa
                        ? portalNacionalUrl(empresa.external_id, range, n.numero)
                        : null
                    }
                  />
                );
              })}
            </Tbody>
          </Table>
        </Box>
        {totalPag > 1 && (
          <Flex justify="space-between" align="center" mt={3} fontSize="12.5px" color="#475569">
            <Text>
              {total} nota(s) · página <b>{pag + 1}</b> de <b>{totalPag}</b>
            </Text>
            <Flex gap={2}>
              <Button
                size="xs"
                variant="ghost"
                isDisabled={pag === 0 || notas.isFetching}
                onClick={() => setPag(p => Math.max(0, p - 1))}
              >
                Anterior
              </Button>
              <Button
                size="xs"
                variant="ghost"
                isDisabled={pag + 1 >= totalPag || notas.isFetching}
                onClick={() => setPag(p => p + 1)}
              >
                Próxima
              </Button>
            </Flex>
          </Flex>
        )}
      </>
    );
  };

  return (
    <Drawer isOpen={!!empresa} placement="right" onClose={onClose} size="xl">
      <DrawerOverlay />
      <DrawerContent bg="rgba(255,255,255,0.9)" backdropFilter="blur(24px)">
        <DrawerCloseButton />
        <DrawerHeader borderBottom="1px solid rgba(15,23,42,0.06)">
          <Text fontSize="11px" color="#94A3B8" fontWeight={600}>
            ID {empresa?.external_id} · Conferência nota-a-nota
          </Text>
          <Text fontSize="17px" fontWeight={750} letterSpacing="-0.02em">
            {empresa?.razao_social}
          </Text>
          <Text fontSize="11px" color="#94A3B8" fontWeight={500} mt={0.5}>
            match por número + série + modelo — o Domínio não tem chave de acesso
          </Text>
          <Tooltip
            hasArrow
            placement="bottom-start"
            label="NFS-e de entrada só entra na comparação depois que a re-extração de impostos do Domínio processa a nota (extract_impostos_nota_entrada). Uma nota já importada no Domínio pode aparecer aqui como 'só no Portal/Autmais' até a re-extração cobrir ela — não é nota perdida, é atraso de processamento fora desta tela."
          >
            <Flex align="center" gap={1} mt={1.5} w="fit-content" cursor="help">
              <WarningCircle size={13} weight="fill" color="#F59E0B" />
              <Text fontSize="11px" color="#B45309" fontWeight={600}>
                Entrada de NFS-e pode ter atraso de re-extração
              </Text>
            </Flex>
          </Tooltip>
        </DrawerHeader>
        <DrawerBody px={4} py={4}>
          {/* abas + export */}
          <Flex justify="space-between" align="center" mb={4} gap={3} flexWrap="wrap">
            <Flex gap={1} bg="rgba(15,23,42,0.04)" borderRadius="11px" p={1} display="inline-flex">
              {ABAS.map(a => (
                <Box
                  key={a.k}
                  as="button"
                  px={4}
                  py={2}
                  borderRadius="8px"
                  fontSize="13px"
                  fontWeight={600}
                  onClick={() => setAba(a.k)}
                  bg={aba === a.k ? 'white' : 'transparent'}
                  color={aba === a.k ? '#0F172A' : '#475569'}
                  boxShadow={aba === a.k ? '0 4px 14px -8px rgba(27,136,245,0.3)' : 'none'}
                >
                  {a.lab}
                </Box>
              ))}
            </Flex>
            <Button
              size="sm"
              variant="outline"
              leftIcon={<DownloadSimple size={15} weight="bold" />}
              onClick={exportar}
              isLoading={exportando}
              loadingText="Gerando…"
              borderColor="rgba(15,23,42,0.12)"
              color="#334155"
              fontSize="12.5px"
              fontWeight={600}
              _hover={{ bg: 'rgba(27,136,245,0.06)', borderColor: '#1B88F5', color: '#1B88F5' }}
            >
              Exportar CSV
            </Button>
            {empresa && (
              <Button
                as="a"
                href={auditoriaDominioUrl(empresa.empresa_id, range, aba)}
                target="_blank"
                rel="noreferrer"
                size="sm"
                variant="outline"
                rightIcon={<ArrowSquareOut size={15} weight="bold" />}
                borderColor="rgba(27,136,245,0.25)"
                color="#1B88F5"
                fontSize="12.5px"
                fontWeight={600}
                _hover={{ bg: 'rgba(27,136,245,0.06)', borderColor: '#1B88F5' }}
              >
                Auditoria (Domínio)
              </Button>
            )}
            {aba === 'servico' && empresa && (
              <Button
                as="a"
                href={portalNacionalUrl(empresa.external_id, range)}
                target="_blank"
                rel="noreferrer"
                size="sm"
                variant="outline"
                rightIcon={<ArrowSquareOut size={15} weight="bold" />}
                borderColor="rgba(109,40,217,0.25)"
                color="#6d28d9"
                fontSize="12.5px"
                fontWeight={600}
                _hover={{ bg: 'rgba(109,40,217,0.06)', borderColor: '#6d28d9' }}
              >
                Portal Nacional
              </Button>
            )}
          </Flex>

          {kpi && (
            <Flex gap={2} mb={3} wrap="wrap">
              {montaChips(kpi, fonteLab, fonteCor).map(c => (
                <KpiChip
                  key={c.k}
                  c={c}
                  ativo={ladoFiltro === c.k}
                  onClick={() => clicaChip(c.k)}
                />
              ))}
            </Flex>
          )}

          {/* Situação: responde "sei que tem N canceladas, mas quais são?".
              Só aparece quando há o que filtrar, pra não virar ruído. */}
          {kpiSit && (kpiSit.canceladas > 0 || kpiSit.divergentes > 0) && (
            <Flex gap={2} mb={3} wrap="wrap" align="center">
              <Text fontSize="10.5px" color="#94A3B8" fontWeight={700} textTransform="uppercase">
                Situação
              </Text>
              {SITUACOES.map(s => {
                const valor = kpiSit[s.kpi];
                if (!valor && situacao !== s.k) return null;
                return (
                  <KpiChip
                    key={s.k}
                    c={{ k: s.k as never, lab: s.lab, cor: s.cor, valor, dica: s.dica }}
                    ativo={situacao === s.k}
                    onClick={() => clicaSituacao(s.k)}
                  />
                );
              })}
            </Flex>
          )}

          {/* busca por coluna (modo + input) + tipo de nota */}
          <Flex gap={2} mb={4} wrap="wrap" align="center">
            <Flex flex="1 1 260px" minW="220px">
              <Select
                value={buscaCampo}
                onChange={e => {
                  setBuscaCampo(e.target.value as BuscaCampo);
                  setPag(0);
                }}
                size="sm"
                w="auto"
                minW="116px"
                borderRightRadius={0}
                borderRight="none"
                bg="white"
                fontWeight={600}
                fontSize="12.5px"
              >
                {BUSCA_MODOS.map(m => (
                  <option key={m.v} value={m.v}>
                    {m.lab}
                  </option>
                ))}
              </Select>
              <Input
                value={buscaInput}
                onChange={e => setBuscaInput(e.target.value)}
                placeholder={`Buscar por ${
                  BUSCA_MODOS.find(m => m.v === buscaCampo)?.lab.toLowerCase() ?? '...'
                }`}
                size="sm"
                borderLeftRadius={0}
                bg="white"
                fontSize="12.5px"
              />
            </Flex>
            {aba !== 'servico' && (
              <Select
                value={tipoDoc}
                onChange={e => {
                  setTipoDoc(e.target.value);
                  setPag(0);
                }}
                size="sm"
                w="auto"
                minW="140px"
                bg="white"
                fontWeight={600}
                fontSize="12.5px"
              >
                {TIPO_DOC_OPCOES.map(t => (
                  <option key={t.v} value={t.v}>
                    {t.lab}
                  </option>
                ))}
              </Select>
            )}
          </Flex>

          {renderCorpo()}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
